<template>
  <!-- Componente invisible que maneja SEO dinámico -->
  <div style="display: none;"></div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { getColivingSearchPageSchema } from './schemas/ColivingSearchSchema.js'
import { isStudentCitySeoIndexed } from './data/cityData.js'
import { SITE_METRICS } from '@/config/siteMetrics'
import { resolveCitySlug, cityHasEnglish, getRegionForCitySlug } from '../../../shared/locationRegistry.cjs'
import {
  RESIDENCIAS_HUB_PATH,
  isResidenciasSearchPath,
  isResidenciasCitySlugSeoIndexed,
} from '../../../shared/residenciasSeo.cjs'
import {
  isAccommodationTypeSearchPath,
  getAccommodationTypeCitySlugFromPath,
  buildColivingCanonicalUrl,
} from '../../../shared/accommodationTypeSeo.cjs'
import { shouldNoIndexProviderOnEnglishPath, providerCanonicalOnEnglishPath } from '../../../shared/providerSearchSeo.cjs'
import { listingsCount, listingsLabel, operatorsCount } from '../../../shared/londonMarketStats.cjs'
import { listingsCount as NYC_SEO_LISTINGS, listingsLabel as NYC_SEO_LISTINGS_LABEL } from '../../../shared/nycMarketStats.cjs'
import { CITY_SEO, getCityPriceDisplay } from '../../../shared/citySeoData.cjs'
import { getGscSearchMeta } from '../../../shared/gscSearchIntent.cjs'

const LONDON_SEO_LISTINGS = listingsCount
const LONDON_SEO_LISTINGS_LABEL = listingsLabel
const LONDON_SEO_OPERATORS = operatorsCount

const SEO_ROOMS_ES = SITE_METRICS.roomCountLabel
const SEO_ROOMS_EN = SITE_METRICS.roomCountLabelEn
const SEO_CITY_COUNT = SITE_METRICS.cityCount
const SEO_MIN_PRICE_ES = SITE_METRICS.minPrice
const SEO_MIN_PRICE_MARKETING_ES = SITE_METRICS.minPriceMarketing
const SEO_MIN_PRICE_EN = SITE_METRICS.minPriceEn

const props = defineProps({
  searchLocation: {
    type: String,
    default: ''
  },
  searchResults: {
    type: Array,
    default: () => []
  },
  searchPerformed: {
    type: Boolean,
    default: false
  },
  currentCity: {
    type: String,
    default: null
  },
  isCitySpecific: {
    type: Boolean,
    default: false
  },
  // Nuevos props para SEO de precio
  maxPrice: {
    type: Number,
    default: null
  },
  priceSlug: {
    type: String,
    default: null
  },
  // Nuevos props para SEO de proveedor
  currentProvider: {
    type: String,
    default: null
  },
  isProviderSearch: {
    type: Boolean,
    default: false
  },
  // Total real de resultados disponibles (paginación) — usado en meta descriptions
  totalResults: {
    type: Number,
    default: 0
  },
  // Prop para resultados de búsqueda (usado para ItemList schema)
  // Solo primeros 10 resultados para evitar schemas gigantes
  searchResultsForSchema: {
    type: Array,
    default: () => []
  }
})

const route = useRoute()

// Convierte nombre de ciudad a slug URL (ej: "San Sebastián" → "san-sebastian")
// Slugs canónicos especiales: nombres de ciudad que colisionan con proveedores u otras rutas
const CANONICAL_CITY_SLUGS = {
  'queens': 'queens-nyc',  // 'queens' en el router ES es el proveedor Queens Coliving España
};

function cityToSlug(cityName) {
  if (!cityName) return '';
  const slug = cityName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');
  return CANONICAL_CITY_SLUGS[slug] ?? slug;
}

// Detectar idioma de la página actual
const currentLanguage = computed(() => {
  const path = route.path.toLowerCase();
  return path.includes('/findyourcoliving') ? 'en' : 'es';
});

// Ciudades que pertenecen a la región US
// Solo slugs de ruta EN — no incluir 'queens' porque en /encuentratucoliving/queens es el proveedor español
const US_CITIES = new Set(['new york', 'new-york', 'manhattan', 'brooklyn', 'queens-nyc', 'jersey city', 'jersey-city', 'williamsburg', 'astoria']);

// Barrios y ciudad principal de la región UK (Londres)
const UK_CITIES = new Set([
  'london', 'canary wharf', 'canary-wharf', 'wembley', 'hounslow',
  'west hampstead', 'west-hampstead', 'notting hill', 'notting-hill',
  'marylebone', 'battersea', 'harrow', 'earlsfield',
  'westbourne park', 'westbourne-park', 'primrose hill', 'primrose-hill',
  'belsize park', 'belsize-park', 'elephant and castle', 'elephant-and-castle',
  'clapham common', 'clapham-common', 'finsbury park', 'finsbury-park',
  'maida vale', 'maida-vale', 'tooting', 'west kensington', 'west-kensington',
  'shoreditch', 'camden', 'hackney', 'brixton', 'limehouse',
  'hammersmith', 'mile end', 'mile-end', 'canada water', 'canada-water',
  'bloomsbury', 'kings cross', 'kings-cross',
  'walthamstow',
  'clapham junction', 'clapham-junction', 'stockwell',
  'east putney', 'east-putney', 'fulham', 'new malden', 'new-malden',
  'clapham',
  'royal docks', 'royal-docks', 'canning town', 'canning-town',
  'poplar', 'stepney green', 'stepney-green', 'lewisham', 'hayes', 'epsom',
  // Chapter Living / aparto (P1)
  'islington', 'white city', 'white-city', 'london bridge', 'london-bridge',
  'westminster', 'south bank', 'south-bank', 'portobello', 'ealing', 'kingston',
  'deptford', 'southwark', 'south bermondsey', 'south-bermondsey',
  'new cross', 'new-cross', 'colindale', 'acton', 'southall',
  'brondesbury',
  'hoxton', 'aldgate', 'bankside', 'highbury', 'greenwich', 'vauxhall', 'tufnell park', 'tufnell-park'
]);

// Extraer el primer segmento de la ruta después del prefijo base
function extractCitySlug(path) {
  const withoutBase = path
    .replace('/findyourcoliving/', '')
    .replace('/encuentratucoliving/', '');
  return withoutBase.split('/')[0]; // solo el primer segmento, ignora sub-rutas tipo /precio
}

function getCitySlugForHreflang() {
  if (props.isCitySpecific && props.currentCity) {
    return resolveCitySlug(cityToSlug(props.currentCity));
  }
  const slug = extractCitySlug(route.path.toLowerCase());
  return slug ? resolveCitySlug(slug) : null;
}

// Detectar si estamos en región US (ciudad NYC o proveedor US)
const isUSRegion = computed(() => {
  if (props.currentCity && US_CITIES.has(props.currentCity.toLowerCase())) return true;
  const path = route.path.toLowerCase();
  return US_CITIES.has(extractCitySlug(path));
});

// Detectar si estamos en región UK (ciudad Londres o barrio londinense)
const isUKRegion = computed(() => {
  if (props.currentCity && UK_CITIES.has(props.currentCity.toLowerCase())) return true;
  const path = route.path.toLowerCase();
  return UK_CITIES.has(extractCitySlug(path));
});

// Detectar si es una página de residencias de estudiantes
// Estas páginas tienen su propio canonical y títulos específicos para estudiantes
const isStudentSearch = computed(() => {
  const path = route.path.toLowerCase();
  return isResidenciasSearchPath(path) || path.startsWith('/residencias-estudiantes');
});

/** Landings por intención — noindex, canonical al marketplace B2C (estrategia anti-duplicados GSC) */
const isAccommodationTypeSearch = computed(() => isAccommodationTypeSearchPath(route.path));

/** Madrid/Barcelona indexables; resto noindex → canonical a coliving ciudad */
const isIndexedStudentPage = computed(() => {
  if (!isStudentSearch.value) return false;
  return isStudentCitySeoIndexed(props.currentCity);
});

// Detectar si venimos de una keyword genérica de alta demanda
// Set de ciudades US/UK que tienen tratamiento de precios/títulos especiales.
// Se usan para detectar la región por CIUDAD en lugar de solo por IP,
// garantizando que el prerendering (que corre desde localhost sin IP regional)
// capture los títulos correctos (con "No Broker Fee", £, etc.)
const US_CITY_NAMES = new Set(['New York', 'Manhattan', 'Brooklyn', 'Queens', 'Jersey City', 'Williamsburg', 'Astoria'])
const UK_CITY_NAMES = new Set([
  'London', 'Wembley', 'Royal Docks', 'Poplar', 'Canning Town', 'Walthamstow',
  'Stepney Green', 'Lewisham', 'Hayes', 'Epsom', 'Brixton', 'Elephant and Castle',
  'West Hampstead', 'Canary Wharf', 'Battersea', 'Hammersmith', 'Shoreditch',
  'Bloomsbury', 'Kings Cross', 'Hackney', 'Camden',
  // Chapter Living / aparto (P1)
  'Islington', 'White City', 'London Bridge', 'Westminster', 'South Bank', 'Portobello', 'Ealing', 'Kingston',
  // Barrios con tráfico GSC — necesarios para isUKCityPage → noindex en ruta ES
  'Notting Hill', 'Clapham', 'Greenwich', 'Vauxhall', 'Fulham',
])

// Detecta ciudad US por nombre (funciona durante prerendering sin IP)
const isUSCityPage = computed(() =>
  !!(props.currentCity && US_CITY_NAMES.has(props.currentCity))
)
// Detecta ciudad UK por nombre (funciona durante prerendering sin IP)
const isUKCityPage = computed(() =>
  !!(props.currentCity && UK_CITY_NAMES.has(props.currentCity))
)

const isBaseColivingSearchPath = computed(() => {
  const path = route.path.toLowerCase();
  return path === '/encuentratucoliving' || path === '/findyourcoliving';
});

const isGenericKeywordSearch = computed(() => {
  const path = route.path.toLowerCase();
  // Páginas de ciudad (/encuentratucoliving/madrid, etc.) nunca son keyword landing.
  if (props.isCitySpecific && props.currentCity) return false;
  // La ruta canónica /encuentratucoliving contiene "encuentra" — usar título base, no keyword.
  if (isBaseColivingSearchPath.value) return false;
  return path.includes('encuentra') ||
         path.includes('buscar') ||
         path.includes('encontrar') ||
         path.includes('barato') ||
         path.includes('economico') ||
         path.includes('findyourcoliving');
});

const genericKeywordType = computed(() => {
  const path = route.path.toLowerCase();
  if (path.includes('barato') || path.includes('economico')) return 'barato';
  if (path.includes('buscar')) return 'buscar';
  if (path.includes('encontrar')) return 'encontrar';
  if (path.includes('encuentra')) return 'encuentra';
  if (path.includes('findyourcoliving')) return 'find';
  return null;
});

// URL base del sitio
const siteUrl = computed(() => {
  return import.meta.env?.VITE_SITE_URL || 'https://coolliving.io'
})

/** Ciudad ES accedida por /findyourcoliving sin versión EN indexable → evita duplicados en GSC */
const isSpanishCityWithoutEnglishPath = computed(() => {
  if (currentLanguage.value !== 'en' || !props.isCitySpecific || !props.currentCity) return false
  const slug = cityToSlug(props.currentCity)
  if (getRegionForCitySlug(slug) !== 'es') return false
  return !cityHasEnglish(slug)
})

/** Proveedor ES en /findyourcoliving/:provider → consolidar en /encuentratucoliving/:provider */
const isSpanishProviderOnEnglishPath = computed(() =>
  shouldNoIndexProviderOnEnglishPath(route.path, props.currentProvider)
)

// Canonical URL dinámica (siempre apunta a la versión del idioma actual)
// ESTRATEGIA CANÓNICA:
//   - /encuentratucoliving/:provider          → canonical a sí misma (página indexable)
//   - /encuentratucoliving/:provider/:ciudad  → canonical a /encuentratucoliving/:ciudad
//   - /encuentratucoliving/:ciudad/:precio    → canonical a /encuentratucoliving/:ciudad
const canonicalUrl = computed(() => {
  // Landings por tipo de alojamiento → siempre canonical a /encuentratucoliving[/ciudad]
  if (isAccommodationTypeSearch.value) {
    const citySlug = props.currentCity
      ? cityToSlug(props.currentCity)
      : getAccommodationTypeCitySlugFromPath(route.path);
    return buildColivingCanonicalUrl(siteUrl.value, citySlug);
  }

  // Páginas de residencias de estudiantes
  if (isStudentSearch.value) {
    const citySlug = props.currentCity ? cityToSlug(props.currentCity) : '';
    if (citySlug && !isResidenciasCitySlugSeoIndexed(citySlug) && !isStudentCitySeoIndexed(props.currentCity)) {
      return `${siteUrl.value}${RESIDENCIAS_HUB_PATH}`;
    }
    const city = citySlug ? `/${citySlug}` : '';
    return `${siteUrl.value}${RESIDENCIAS_HUB_PATH}${city}`;
  }

  // Ciudad UK/US accedida por /encuentratucoliving/... → canonical a ruta EN (GSC: london, walthamstow…)
  if ((isUSCityPage.value || isUKCityPage.value) && props.isCitySpecific && props.currentCity) {
    return `${siteUrl.value}/findyourcoliving/${cityToSlug(props.currentCity)}`;
  }

  // Región US → canonical siempre a /findyourcoliving/ (inglés, mercado objetivo)
  if (isUSRegion.value) {
    const baseUrlUS = `${siteUrl.value}/findyourcoliving`;
    if (props.currentCity) {
      const citySlug = cityToSlug(props.currentCity);
      return `${baseUrlUS}/${citySlug}`;
    }
    return `${baseUrlUS}/new-york`;
  }

  // Región UK → canonical siempre a /findyourcoliving/ (inglés, mercado objetivo)
  if (isUKRegion.value) {
    const baseUrlUK = `${siteUrl.value}/findyourcoliving`;
    if (props.currentCity) {
      const citySlug = cityToSlug(props.currentCity);
      return `${baseUrlUK}/${citySlug}`;
    }
    return `${baseUrlUK}/london`;
  }

  const baseSlug = currentLanguage.value === 'en' ? 'findyourcoliving' : 'encuentratucoliving';
  const baseUrl = `${siteUrl.value}/${baseSlug}`;

  // Ciudad ES sin versión EN en /findyourcoliving → canonical a la ruta ES indexable
  if (isSpanishCityWithoutEnglishPath.value && props.currentCity) {
    return `${siteUrl.value}/encuentratucoliving/${cityToSlug(props.currentCity)}`;
  }

  // Proveedor ES en /findyourcoliving → canonical a ruta ES
  if (isSpanishProviderOnEnglishPath.value && props.currentProvider) {
    const canonical = providerCanonicalOnEnglishPath(siteUrl.value, props.currentProvider);
    if (canonical) return canonical;
  }
  
  // Si estamos en una búsqueda por PROVEEDOR
  if (props.isProviderSearch && props.currentProvider) {
    // Proveedor + ciudad → canonical a la página de SOLO ciudad
    // Esto consolida autoridad SEO en las páginas prerenderizadas por ciudad
    if (props.currentCity) {
      return `${baseUrl}/${cityToSlug(props.currentCity)}`
    }
    
    // Proveedor SIN ciudad → canonical a sí misma (es contenido único del proveedor)
    return `${baseUrl}/${props.currentProvider.toLowerCase()}`
  }
  
  // Si estamos en una ruta específica de ciudad
  if (props.isCitySpecific && props.currentCity) {
    // IMPORTANTE: Las páginas con filtros de precio (hasta-700, economicos, baratos)
    // canonizan a la página de ciudad base para consolidar autoridad SEO
    return `${baseUrl}/${cityToSlug(props.currentCity)}`
  }
  
  // Fallback: Para búsquedas genéricas, usar la ruta base limpia
  // NO usar query params para evitar que Google indexe URLs sucias
  if (props.searchLocation && !props.isCitySpecific) {
    // Intentar normalizar la ubicación como una ciudad
    const normalizedLocation = cityToSlug(props.searchLocation)
    const validCities = ['madrid', 'barcelona', 'valencia', 'sevilla', 'bilbao', 'málaga', 'malaga', 'salamanca', 'alicante', 'granada', 'pamplona']
    
    if (validCities.includes(normalizedLocation)) {
      return `${baseUrl}/${normalizedLocation}`
    }
    
    // Si no es una ciudad conocida, usar la ruta base (sin query params)
    return baseUrl
  }
  
  return baseUrl
})

// URLs alternativas para hreflang (versiones ES/EN de la misma página)
// IMPORTANTE: Coherente con canonical — proveedor+ciudad apunta al hreflang de solo ciudad
const alternateUrls = computed(() => {
  const spanishBase = `${siteUrl.value}/encuentratucoliving`;
  const englishBase = `${siteUrl.value}/findyourcoliving`;

  // Región US → hreflang solo en (no tiene versión española)
  if (isUSRegion.value) {
    const citySlug = props.currentCity
      ? `/${cityToSlug(props.currentCity)}`
      : '/new-york';
    const usUrl = `${englishBase}${citySlug}`;
    return { en: usUrl, 'x-default': usUrl };
  }

  // Región UK → hreflang en-GB (mercado británico) + en + x-default (sin versión española)
  if (isUKRegion.value) {
    const citySlug = props.currentCity
      ? `/${cityToSlug(props.currentCity)}`
      : '/london';
    const ukUrl = `${englishBase}${citySlug}`;
    return { 'en-GB': ukUrl, en: ukUrl, 'x-default': ukUrl };
  }

  // Landings por intención — hreflang coherente con canonical (marketplace B2C)
  if (isAccommodationTypeSearch.value) {
    const slug = props.currentCity
      ? cityToSlug(props.currentCity)
      : getAccommodationTypeCitySlugFromPath(route.path);
    const pathSuffix = slug ? `/${slug}` : '';
    const spanishUrl = `${spanishBase}${pathSuffix}`;
    if (slug && !cityHasEnglish(slug)) {
      return { es: spanishUrl, 'x-default': spanishUrl };
    }
    return {
      es: spanishUrl,
      en: `${englishBase}${pathSuffix}`,
      'x-default': spanishUrl,
    };
  }
  
  let pathSuffix = '';
  
  // Construir el sufijo de la ruta
  // IMPORTANTE: Hreflang debe ser coherente con canonical
  if (props.isProviderSearch && props.currentProvider) {
    // Proveedor + ciudad → hreflang apunta a la página de solo ciudad (coherente con canonical)
    if (props.currentCity) {
      pathSuffix = `/${cityToSlug(props.currentCity)}`;
    } else {
      // Proveedor SIN ciudad → hreflang apunta a la página del proveedor (coherente con canonical)
      pathSuffix = `/${props.currentProvider.toLowerCase()}`;
    }
  } else if (props.isCitySpecific && props.currentCity) {
    // Página de ciudad (con o sin filtro de precio) → hreflang apunta a ciudad base
    pathSuffix = `/${cityToSlug(props.currentCity)}`;
  }

  const citySlug = getCitySlugForHreflang();
  const hasEnglishVersion = !citySlug || cityHasEnglish(citySlug);
  const spanishUrl = `${spanishBase}${pathSuffix}`;

  if (!hasEnglishVersion) {
    return {
      es: spanishUrl,
      'x-default': spanishUrl,
    };
  }

  return {
    es: spanishUrl,
    en: `${englishBase}${pathSuffix}`,
    // x-default apunta a español como idioma por defecto
    'x-default': spanishUrl,
  };
})

// Descripción de precio para SEO
const priceDescription = computed(() => {
  if (!props.maxPrice) return ''
  if (props.maxPrice < 0) return 'premium'
  if (props.maxPrice <= 500) return 'baratos'
  if (props.maxPrice <= 700) return 'económicos'
  if (props.maxPrice <= 1000) return 'a buen precio'
  if (props.maxPrice <= 1500) return 'premium'
  return 'de lujo'
})

// Label legible para precio en títulos SEO (noindex, solo UX)
const priceLabel = computed(() => {
  if (!props.maxPrice) return '';
  if (props.maxPrice < 0) return `más de ${Math.abs(props.maxPrice)}€`;
  return `hasta ${props.maxPrice}€`;
})

const priceLabelEN = computed(() => {
  if (!props.maxPrice) return '';
  if (props.maxPrice < 0) return `over €${Math.abs(props.maxPrice)}`;
  return `up to €${props.maxPrice}`;
})

// Datos de proveedores para SEO
const providerData = {
  'urbancampus': { name: 'Urban Campus', avgPrice: 850 },
  'gaiastays': { name: 'GaiaStays', avgPrice: 700 },
  'kanso': { name: 'Kanso Coliving', avgPrice: 650 },
  'muppy': { name: 'Muppy', avgPrice: 600 },
  'enso': { name: 'Enso Coliving', avgPrice: 680 },
  'homiii': { name: 'Homiii', avgPrice: 550 },
  'spacest': { name: 'Spacest', avgPrice: 900 },
  'aticco': { name: 'Aticco Living', avgPrice: 800 },
  'haaus': { name: 'Haaus', avgPrice: 700 },
  'socien': { name: 'Socien', avgPrice: 620 },
  'suiters': { name: 'Suiters', avgPrice: 750 },
  'houxury': { name: 'Houxury', avgPrice: 1100 },
  'colivinn': { name: 'ColivINN', avgPrice: 600 },
  'trc': { name: 'The Residential Club', avgPrice: 1200 },
  'node': { name: 'Node Living', avgPrice: 750 },
  'colive': { name: 'Colive', avgPrice: 450 },
  'trees': { name: 'Trees Coliving', avgPrice: 670 },
  'resa': { name: 'RESA', avgPrice: 850 },
  'yugo': { name: 'Yugo', avgPrice: 900 },
  'livensa': { name: 'Livensa Living', avgPrice: 820 },
  'collegiate': { name: 'Collegiate', avgPrice: 950 },
  'flipco': { name: 'Flipco', avgPrice: 700 },
  'liveit': { name: 'Live It', avgPrice: 750 },
  'habyt': { name: 'Habyt', avgPrice: 800 },
  'queens': { name: 'Queens', avgPrice: 650 },
  'socialhub': { name: 'The Social Hub', avgPrice: 950 },
  'outsite': { name: 'Outsite', avgPrice: 1275 },
  'mycoliving': { name: 'MycoLiving', avgPrice: 600 },
  'becasa': { name: 'Becasa', avgPrice: 550 },
  'oceanika': { name: 'Oceanika', avgPrice: 600 },
  'nova': { name: 'Nova Coliving', avgPrice: 500 },
  'alegoria': { name: 'Alegoría', avgPrice: 700 },
  'olivar': { name: 'Olivar Plaza', avgPrice: 650 },
  'thestart': { name: 'The Start', avgPrice: 1050 },
  'flaternity': { name: 'Flaternity', avgPrice: 600 },
  'hive': { name: 'Grupo Hive', avgPrice: 1800 },
  'nomad': { name: 'Nomad Flex Living', avgPrice: 1200 },
  'farhome': { name: 'Far Home', avgPrice: 550 },
  'repeople': { name: 'Repeople', avgPrice: 700 },
  'maraya': { name: 'Maraya', avgPrice: 650 },
  'aspasios': { name: 'Aspasios Boutique Apartments', avgPrice: 1800 },
  // Proveedores US - Nueva York
  'junehomes': { name: 'June Homes', avgPrice: 2200 },
  'furnishedquarters': { name: 'Furnished Quarters', avgPrice: 3500 },
  'fllat': { name: 'Fllat', avgPrice: 2800 },
  'aya': { name: 'Hello AYA', avgPrice: 2500 },
  'cohabs': { name: 'Cohabs', avgPrice: 2000 },
  'outpost': { name: 'Outpost', avgPrice: 2600 },
  'roomrs': { name: 'Roomrs', avgPrice: 1800 },
  'nodenyc': { name: 'Node Living NYC', avgPrice: 2500 },
  'silverman': { name: 'Silverman Building', avgPrice: 2400 },
  'reserve': { name: 'The Reserve NYC', avgPrice: 2200 },
  'squarelivinghub': { name: 'The Square Living Hub', avgPrice: 2300 },
  'blueground': { name: 'Blueground', avgPrice: 3200 },
  'common': { name: 'Common', avgPrice: 1650 },
  'bungalow': { name: 'Bungalow', avgPrice: 1250 },
  'outsitenyc': { name: 'Outsite', avgPrice: 2400 },
  'ollie': { name: 'Ollie', avgPrice: 2200 },
  'sharedeasy': { name: 'SharedEasy', avgPrice: 1200 },
  // Proveedores UK - Londres (precio en £)
  'arkliving': { name: 'Ark Living', avgPrice: 1450 },
  'gravityco': { name: 'Gravity Co', avgPrice: 1550 },
  'folkcoliving': { name: 'Folk Coliving', avgPrice: 1400 },
  'masonfifth': { name: 'Mason & Fifth', avgPrice: 1480 },
  'cohabslondon': { name: 'Cohabs London', avgPrice: 1420 },
  'nodelondon':       { name: 'Node Living London', avgPrice: 1880 },
  'quintainliving':   { name: 'Quintain Living', avgPrice: 2200 },
  'scape':            { name: 'Scape', avgPrice: 1650 },
  'canvas':           { name: 'Canvas', avgPrice: 1300 },
  'lyvly':            { name: 'Lyvly', avgPrice: 1310 },
  'fizzyliving':      { name: 'Fizzy Living', avgPrice: 1950 },
  'vonder':           { name: 'Vonder', avgPrice: 3400 },
  'chapter':          { name: 'Chapter Living', avgPrice: 1450 },
  'aparto':           { name: 'aparto', avgPrice: 1350 },
  'yourtribe':        { name: 'YourTRIBE', avgPrice: 1450 },
  'uncle':            { name: 'UNCLE', avgPrice: 2200 },
  'dandi':            { name: 'Dandi Living', avgPrice: 1650 },
  'iq':               { name: 'iQ Student', avgPrice: 1750 },
  'unite':            { name: 'Unite Students', avgPrice: 1300 },
  'bloom':            { name: 'Bloom Coliving', avgPrice: 1250 },
  'bamboo':           { name: 'Bamboo Living', avgPrice: 3200 },
  'stayclub':         { name: 'The Stay Club', avgPrice: 1750 },
  'coluxliving':      { name: 'Co-Lux Living', avgPrice: 920 },
  'thecollective':    { name: 'The Collective', avgPrice: 1560 },
  'studentroost':     { name: 'Student Roost', avgPrice: 1350 },
  'domicoliving':     { name: 'Domi Coliving', avgPrice: 975 }
};

// Datos específicos por ciudad para SEO
// IMPORTANTE: seoCount y precios se leen de shared/citySeoData.cjs (fuente única).
// adjective y description son presentacionales y viven aquí.
// NO usar contadores dinámicos (totalResults, searchResults.length) en <title> — crean
// centenares de variaciones ("336+", "337+", "338+") que Google interpreta como duplicados.

// Adjectives / descriptions that are presentation-only (not duplicated elsewhere)
const cityPresentation = {
  'Madrid':      { adjective: 'la capital', description: 'el corazón financiero y cultural de España' },
  'Barcelona':   { adjective: 'la ciudad condal', description: 'el centro de innovación y cultura mediterránea' },
  'Valencia':    { adjective: 'la ciudad del Turia', description: 'la perfecta combinación de playa y ciudad' },
  'Sevilla':     { adjective: 'la perla de Andalucía', description: 'el alma del flamenco y la tradición española' },
  'Bilbao':      { adjective: 'la villa', description: 'el centro vasco de arte y gastronomía' },
  'Málaga':      { adjective: 'la capital de la Costa del Sol', description: 'el paraíso mediterráneo con clima perfecto' },
  'Zaragoza':    { adjective: 'la ciudad del Ebro', description: 'el corazón de Aragón con historia milenaria' },
  'Salamanca':   { adjective: 'la ciudad universitaria', description: 'la cuna del conocimiento con siglos de historia académica' },
  'Alicante':    { adjective: 'la joya del Mediterráneo', description: 'sol, playa y calidad de vida en la costa levantina' },
  'Granada':     { adjective: 'la ciudad de la Alhambra', description: 'la fusión perfecta entre historia árabe y vida universitaria' },
  'Pamplona':    { adjective: 'la ciudad de los Sanfermines', description: 'tradición navarra y excelente calidad de vida' },
  'New York':    { adjective: 'the city that never sleeps', description: `the most exciting city in the world with ${NYC_SEO_LISTINGS} verified coliving rooms` },
  'Manhattan':   { adjective: 'the heart of New York City', description: 'the most iconic borough, home to Midtown, Downtown and Harlem' },
  'Brooklyn':    { adjective: 'the creative borough', description: 'vibrant neighborhoods like Williamsburg, DUMBO and Park Slope' },
  'Queens':      { adjective: 'the most diverse borough', description: 'affordable living with easy access to Manhattan via subway' },
  'Jersey City': { adjective: 'across the Hudson from Manhattan', description: 'a growing hub with Manhattan views and lower prices' },
  'Williamsburg':{ adjective: "Brooklyn's most iconic neighborhood", description: "world-class food, rooftop bars and L train to Midtown in 20 min" },
  'Astoria':     { adjective: "Queens' most vibrant and well-connected neighborhood", description: 'great food scene, community feel and N/W train to Times Square in 20 min' },
};

// Build cityData by merging shared numbers + local presentation.
// NYC seoCount overrides come from nycMarketStats for New York (the rest use CITY_SEO).
const cityData = Object.fromEntries(
  Object.entries(cityPresentation).map(([city, pres]) => {
    const seo = CITY_SEO[city] || {};
    const seoCount = city === 'New York' ? NYC_SEO_LISTINGS : (seo.seoCount || 10);
    return [city, {
      adjective: pres.adjective,
      description: pres.description,
      priceRange: getCityPriceDisplay(city),
      seoCount,
    }];
  })
);
// Alias Malaga → Málaga (some routes use accented / unaccented)
cityData['Malaga'] = cityData['Málaga'];

// ── London neighborhoods (presentation-only, no duplication elsewhere) ────
Object.assign(cityData, {
  'London': {
    adjective: 'the world\'s most cosmopolitan city',
    description: `Europe's coliving capital with ${LONDON_SEO_LISTINGS} verified rooms across ${LONDON_SEO_OPERATORS} operators`,
    priceRange: '£800-£2,500',
    seoCount: LONDON_SEO_LISTINGS
  },
  'Elephant and Castle': {
    adjective: 'South London\'s most connected neighbourhood',
    description: 'vibrant regenerated area with fast Northern & Bakerloo line connections',
    priceRange: '£1,391-£1,500',
    seoCount: 9
  },
  'Canary Wharf': {
    adjective: 'London\'s financial district',
    description: 'modern riverside living steps from Europe\'s biggest financial hub',
    priceRange: '£1,400-£1,800',
    seoCount: 1
  },
  'West Hampstead': {
    adjective: 'leafy North London village',
    description: 'boutique shops, bars and excellent Jubilee & Overground connections',
    priceRange: '£1,500-£1,700',
    seoCount: 2
  },
  'Battersea': {
    adjective: 'riverside South West London',
    description: 'iconic power station district with parks and Chelsea views',
    priceRange: '£1,450-£1,600',
    seoCount: 1
  },
  'Finsbury Park': {
    adjective: 'vibrant North London hub',
    description: 'diverse neighbourhood with Victoria, Piccadilly & Overground connections',
    priceRange: '£1,415-£1,550',
    seoCount: 1
  },
  'Clapham Common': {
    adjective: 'South West London\'s social heart',
    description: 'one of London\'s most popular areas with brilliant park and nightlife',
    priceRange: '£1,400-£1,600',
    seoCount: 1
  },
  'Maida Vale': {
    adjective: 'London\'s Little Venice',
    description: 'charming canal-side Victorian neighbourhood in West London',
    priceRange: '£1,500-£1,700',
    seoCount: 1
  },
  'Tooting': {
    adjective: 'South London\'s multicultural gem',
    description: 'affordable, diverse area famous for its food market and Northern line',
    priceRange: '£1,300-£1,500',
    seoCount: 1
  },
  'West Kensington': {
    adjective: 'West London close to Kensington\'s parks',
    description: 'well-connected residential area with District & Piccadilly lines',
    priceRange: '£1,450-£1,600',
    seoCount: 1
  },
  'Wembley': {
    adjective: 'North West London entertainment hub',
    description: 'home to Wembley Stadium with fast Metropolitan line connections',
    priceRange: '£1,300-£1,500',
    seoCount: 1
  },
  'Brixton': {
    adjective: 'creative and vibrant South London',
    description: 'buzzing with markets, live music, great restaurants and direct Victoria line to central London',
    priceRange: '£1,785-£2,562',
    seoCount: 4
  },
  'Limehouse': {
    adjective: 'trendy East London by the river',
    description: 'newly developed hub close to Canary Wharf with DLR and Overground connections',
    priceRange: '£1,742-£2,154',
    seoCount: 4
  },
  'Hammersmith': {
    adjective: 'vibrant West London by the Thames',
    description: 'home to Westfield shopping, riverside pubs, Hammersmith Apollo and easy District/Piccadilly line access',
    priceRange: '£2,138/mo',
    seoCount: 1
  },
  'Mile End': {
    adjective: 'lively East London on Queen Mary campus',
    description: 'situated on QMUL campus, steps from Victoria Park, hip bars and eateries of East London',
    priceRange: '£1,345-£1,512/mo',
    seoCount: 2
  },
  'Canada Water': {
    adjective: 'up-and-coming South East London',
    description: 'Jubilee line hub close to Bermondsey, Peckham and riverside parks, minutes from Canary Wharf',
    priceRange: '£1,490-£1,647/mo',
    seoCount: 3
  },
  'Shoreditch': {
    adjective: 'London\'s coolest creative neighbourhood',
    description: 'art galleries, street food, rooftop bars and Spitalfields Market — 3 min from Old Street station',
    priceRange: '£1,880/mo',
    seoCount: 1
  },
  'Bloomsbury': {
    adjective: 'central London academic heartland',
    description: 'steps from UCL, LSE, The British Museum and Russell Square — the most central London coliving area',
    priceRange: '£1,408-£1,733/mo',
    seoCount: 3
  },
  'Kings Cross': {
    adjective: 'premium central London transport hub',
    description: 'steps from St Pancras International, Granary Square, Soho and the night-time Elizabeth line',
    priceRange: '£2,881/mo',
    seoCount: 1
  },
  'Walthamstow': {
    adjective: 'creative and up-and-coming North East London',
    description: 'Victoria line direct to Oxford Circus in 20 min, vibrant high street market, arts scene and green spaces',
    priceRange: '£1,105-£1,192/mo',
    seoCount: 2
  },
  'Hackney': {
    adjective: 'vibrant and arty East London',
    description: 'London Fields, Victoria Park, Hackney Wick — the coolest east London neighbourhood with Overground connections',
    priceRange: '£1,095-£1,139/mo',
    seoCount: 2
  },
  'Clapham': {
    adjective: 'lively South London favourite',
    description: 'Clapham Common, great pubs, restaurants and Northern line connections — a top choice for young professionals',
    priceRange: '£1,430/mo',
    seoCount: 1
  },
  'Clapham Junction': {
    adjective: 'well-connected South West London hub',
    description: 'one of London\'s busiest transport interchanges with direct trains to Victoria, Waterloo and Gatwick',
    priceRange: '£1,359-£1,450/mo',
    seoCount: 2
  },
  'Stockwell': {
    adjective: 'affordable and well-connected South London',
    description: 'Victoria & Northern lines, close to Brixton and Clapham — great value in a well-connected SW9 postcode',
    priceRange: '£1,370/mo',
    seoCount: 1
  },
  'East Putney': {
    adjective: 'leafy and relaxed South West London',
    description: 'District line to Earl\'s Court and Sloane Square, riverside walks and a quiet residential feel',
    priceRange: '£1,240/mo',
    seoCount: 1
  },
  'Fulham': {
    adjective: 'upmarket West London riverside',
    description: 'Fulham Broadway (District line), King\'s Road Chelsea and Thames riverside walks — premium West London living',
    priceRange: '£1,580/mo',
    seoCount: 1
  },
  'New Malden': {
    adjective: 'quiet and affordable South West London',
    description: 'South Western Railway to Waterloo, family-friendly neighbourhood with excellent Korean restaurants',
    priceRange: '£860/mo',
    seoCount: 1
  },
  'Royal Docks': {
    adjective: 'fast-growing East London riverside',
    description: 'DLR to Canary Wharf in 5 min, next to ExCeL London and London City Airport — Europe\'s most exciting regeneration zone',
    priceRange: '£1,950-£3,100/mo',
    seoCount: 6
  },
  'Canning Town': {
    adjective: 'up-and-coming East London hub',
    description: 'Jubilee & DLR lines to Canary Wharf in 4 min and Bond Street in 16 min — excellent connectivity at affordable prices',
    priceRange: '£1,700-£2,600/mo',
    seoCount: 5
  },
  'Poplar': {
    adjective: 'East London close to Canary Wharf',
    description: 'walking distance from Canary Wharf, DLR to Bank 12 min — a smart choice for finance and tech professionals',
    priceRange: '£1,850-£3,050/mo',
    seoCount: 6
  },
  'Stepney Green': {
    adjective: 'vibrant inner East London',
    description: 'District line to Monument 8 min, steps from Brick Lane, Whitechapel Elizabeth line and Queen Mary University',
    priceRange: '£1,780-£2,380/mo',
    seoCount: 5
  },
  'Lewisham': {
    adjective: 'well-connected South East London',
    description: 'DLR to Canary Wharf 15 min, National Rail to London Bridge 10 min — close to Greenwich, Deptford and Blackheath',
    priceRange: '£1,580-£2,120/mo',
    seoCount: 5
  },
  'Hayes': {
    adjective: 'affordable outer West London',
    description: 'Elizabeth line to Paddington 16 min, Heathrow in 8 min — ideal for airport workers and international commuters',
    priceRange: '£1,450-£2,380/mo',
    seoCount: 7
  },
  'Epsom': {
    adjective: 'peaceful Surrey commuter town',
    description: 'direct trains to Waterloo in 30 min and Victoria in 35 min — green surroundings and excellent value outside Greater London',
    priceRange: '£1,380-£1,920/mo',
    seoCount: 5
  },
  'Islington': {
    adjective: 'vibrant North London with great tube links',
    description: 'Caledonian Road (Piccadilly line), close to UAL Central Saint Martins, City University and the buzzing Angel/Upper Street area',
    priceRange: '£1,446-£1,663/mo',
    seoCount: 4
  },
  'White City': {
    adjective: 'West London next to Westfield and the BBC',
    description: 'Central & Wood Lane (Circle/H&C lines) — steps from Westfield shopping, BBC Studios and Imperial College White City campus',
    priceRange: '£1,684/mo',
    seoCount: 1
  },
  'London Bridge': {
    adjective: 'premium central London near Borough Market',
    description: 'steps from Borough Market, London Bridge station (Jubilee & Northern lines), Tate Modern and the South Bank',
    priceRange: '£1,820/mo',
    seoCount: 1
  },
  'Westminster': {
    adjective: 'iconic Central London by the Thames',
    description: 'near Lambeth Palace, Imperial War Museum and King\'s College London Waterloo campus — Zone 1 with Vauxhall & Waterloo connections',
    priceRange: '£2,102/mo',
    seoCount: 1
  },
  'South Bank': {
    adjective: 'vibrant cultural hub by the Thames',
    description: 'steps from Tate Modern, Shakespeare\'s Globe and Southwark Cathedral — Southwark (Jubilee line), Borough and Waterloo nearby',
    priceRange: '£2,080/mo',
    seoCount: 1
  },
  'Portobello': {
    adjective: 'characterful West London near Notting Hill',
    description: 'Notting Hill Gate or Ladbroke Grove — steps from Portobello Road Market, a favourite for UAL students and creatives',
    priceRange: '£1,036/mo',
    seoCount: 1
  },
  'Ealing': {
    adjective: 'leafy West London on Central & District lines',
    description: 'well-connected Zone 3 suburb with Elizabeth line to Paddington in 12 min — popular with Brunel, UWL and Ealing students',
    priceRange: '£1,187/mo',
    seoCount: 1
  },
  'Kingston': {
    adjective: 'thriving South West London town',
    description: 'riverside market town with Kingston University, excellent shopping and fast trains to Waterloo in 32 min',
    priceRange: '£1,000-£1,400/mo',
    seoCount: 1
  },
  'Camden': {
    adjective: "North London's creative and cultural hub",
    description: 'Camden Market, live music venues and Northern line to King\'s Cross in 8 min',
    priceRange: '£1,600-£2,200',
    seoCount: 6
  },
});

// Títulos optimizados por ciudad (GSC ago 2026). Keyword al inicio + cifra + precio + CTA.
const cityPageTitles = {
  'Madrid': 'Alquiler de Habitaciones en Madrid 2026: 1.400+ Coliving y Apartamentos desde 450€ | Coolliving',
  'Barcelona': 'Habitaciones en Barcelona 2026: 150+ Coliving y Alquiler Flexible desde 450€ | Coolliving',
  'Valencia': 'Habitaciones en Valencia 2026: 60+ Coliving y Flex Living desde 400€ | Coolliving',
  'Málaga': 'Habitaciones en Málaga 2026: 40+ Coliving y Apartamentos desde 400€ | Coolliving',
  'Sevilla': 'Alquiler de Habitaciones en Sevilla 2026: 25+ Coliving desde 300€ | Coolliving',
  'Bilbao': 'Habitaciones en Bilbao 2026: 15+ Coliving y Alquiler Flexible desde 400€ | Coolliving',
  'Salamanca': 'Habitaciones en Salamanca 2026: 20+ Coliving y Residencias desde 280€ | Coolliving',
  'Alicante': 'Habitaciones en Alicante 2026: 15+ Coliving y Flex Living desde 320€ | Coolliving',
  'Granada': 'Habitaciones en Granada 2026: 20+ Coliving y Residencias desde 280€ | Coolliving',
  'Zaragoza': 'Habitaciones en Zaragoza 2026: 10+ Coliving y Alquiler Flexible desde 250€ | Coolliving',
};

const cityPageDescriptions = {
  'Madrid': `Alquiler de habitaciones en Madrid: 1.400+ verificadas en Malasaña, Chamberí y Lavapiés desde 450€/mes todo incluido. Coliving, apartamentos amueblados y alquiler flexible desde 1 mes. Sin fianza abusiva ni comisiones. Reserva 100% online.`,
  'Barcelona': `Habitaciones en alquiler en Barcelona: 150+ verificadas en Gràcia, Eixample, Poblenou y El Born desde 450€/mes todo incluido. Coliving y flex living con contratos desde 1 mes. Sin comisiones de agencia.`,
  'Valencia': `+60 habitaciones en alquiler en Valencia: Ruzafa, El Carmen, Benimaclet. Desde 400€/mes todo incluido (WiFi, luz, agua, limpieza). Coliving, flex living y apartamentos amueblados desde 1 mes. La mejor relación calidad-precio de España. Sin comisión.`,
  'Málaga': `+40 habitaciones en Málaga y flex living spaces: Centro, Soho, Teatinos. Desde 400€/mes todo incluido. Coliving y alquiler flexible para nómadas digitales. 300 días de sol. Sin fianza abusiva ni comisiones.`,
  'Sevilla': `+25 habitaciones en alquiler en Sevilla: Triana, Alameda, Nervión, Santa Cruz. Desde 300€/mes todo incluido. Coliving y alquiler temporal desde 1 mes. Sin comisión de agencia. Reserva online en 5 minutos.`,
  'Bilbao': `+15 habitaciones en Bilbao: Casco Viejo, Abando, Deusto, Indautxu. Desde 400€/mes todo incluido. Coliving y alquiler flexible. La ciudad con mejor gastronomía de España. Sin comisión de agencia.`,
  'Salamanca': `+20 habitaciones en Salamanca para estudiantes y Erasmus. Desde 280€/mes todo incluido. Coliving y residencias cerca de la universidad. Alquiler temporal desde 1 mes. Sin comisiones.`,
  'Alicante': `+15 habitaciones en alquiler en Alicante: Centro, Playa de San Juan, Albufereta. Desde 320€/mes todo incluido. Coliving y flex living desde 1 mes. Sol y playa en la Costa Blanca. Sin comisión de agencia.`,
  'Granada': `+20 habitaciones en Granada para estudiantes: Albaicín, Realejo, Centro. Desde 280€/mes todo incluido. Coliving y alquiler temporal. Tapas gratis, Alhambra y Sierra Nevada. La ciudad universitaria más económica de España.`,
  'Zaragoza': `+10 habitaciones en Zaragoza: Centro, Universidad, Delicias. Desde 250€/mes todo incluido. Coliving y alquiler flexible. AVE a Madrid y Barcelona en 1h15. Los precios más bajos de las grandes ciudades. Sin comisión.`,
};

const studentCityPageTitles = {
  'Madrid': 'Residencias Universitarias Madrid 2026 — RESA, Nido, Xior, Livensa | Coolliving',
  'Barcelona': 'Residencias Universitarias Barcelona 2026 — Livensa, RESA, Nido | Coolliving',
  'Valencia': 'Residencias Universitarias Valencia 2026 — UV, UPV, Erasmus | Coolliving',
  'Málaga': 'Residencias Universitarias Málaga 2026 — UMA, Teatinos | Coolliving',
  'Sevilla': 'Residencias Universitarias Sevilla 2026 — US, Pablo de Olavide | Coolliving',
  'Salamanca': 'Residencias Universitarias Salamanca 2026 — USAL | Coolliving',
};

const studentCityPageDescriptions = {
  'Madrid': 'Compara residencias universitarias en Madrid: RESA, Nido, Xior, Livensa, Collegiate, Yugo y Urban Campus. Habitaciones verificadas en Moncloa, Getafe y corredor universitario. Desde 500€/mes todo incluido. Reserva online sin comisiones.',
  'Barcelona': 'Residencias universitarias en Barcelona cerca de UB, UPF y UPC. Livensa, RESA, Nido, Xior y Urban Campus. Desde 450€/mes todo incluido. Erasmus y curso completo. Sin comisiones.',
  'Valencia': 'Residencias en Valencia para UV y UPV: Livensa, RESA, Nido y Xior en Benimaclet y centro. Desde 380€/mes todo incluido. Compara operadores y reserva sin comisiones.',
  'Málaga': 'Residencias universitarias en Málaga (UMA): operadores verificados en Teatinos y centro. Desde 400€/mes todo incluido. Solo RESA, Nido, Xior, Livensa y similares verificados.',
  'Sevilla': 'Residencias de estudiantes en Sevilla: Universidad de Sevilla y Pablo de Olavide. Desde 350€/mes todo incluido. Compara Livensa, RESA, Nido y Xior sin comisiones.',
  'Salamanca': 'Residencias en Salamanca para USAL y Erasmus. Desde 320€/mes todo incluido. Livensa, RESA, Nido, Xior — reserva online en Coolliving.',
};

// Páginas marketplace US (NYC) — títulos/descriptions con queries GSC (coliving brooklyn, furnished rooms, no broker fee)
const usCityPageTitles = {
  'New York': `Coliving NYC 2026 — ${NYC_SEO_LISTINGS_LABEL} Furnished Rooms in All Boroughs | No Broker Fee | Coolliving`,
  'Brooklyn': 'Coliving Brooklyn 2026 — 71+ Furnished Rooms & Shared Apartments | No Broker Fee | Coolliving',
  'Manhattan': 'Coliving Manhattan NYC 2026 — 163+ Furnished Rooms from $2,000/mo | No Broker Fee | Coolliving',
  'Queens': 'Coliving Queens NYC — 25+ Cheap Furnished Rooms from $1,200/mo | No Broker Fee | Coolliving',
  'Jersey City': 'Coliving Jersey City — Furnished Rooms from $1,500/mo, PATH to Manhattan | Coolliving',
};

const usCityPageDescriptions = {
  'New York': `No broker fee. Compare ${NYC_SEO_LISTINGS} verified coliving rooms and furnished apartments across Manhattan, Brooklyn, Queens and Jersey City. Month-to-month from $1,200/mo all-inclusive (WiFi, utilities, cleaning). Roomrs, June Homes, Common, SharedEasy — book online.`,
  'Brooklyn': 'No broker fee. 71+ furnished coliving rooms and shared apartments in Williamsburg, DUMBO, Bushwick and Park Slope. From $1,500/mo all-inclusive, month-to-month leases. Compare cheap coliving in Brooklyn vs Manhattan — subway to Midtown in 30 min.',
  'Manhattan': 'No broker fee. 163+ furnished coliving rooms in Midtown, Chelsea, UWS, Harlem and Financial District. From $2,000/mo all-inclusive. Skip the $3,000+ broker fee and 12-month lease — flexible coliving in Manhattan, book online.',
  'Queens': 'No broker fee. 25+ affordable coliving rooms in Long Island City, Astoria and Jackson Heights — NYC\'s cheapest furnished rooms from $1,200/mo. Month-to-month, all-inclusive. LIC to Midtown in 5 minutes by subway.',
  'Jersey City': 'No broker fee. Furnished coliving rooms in Newport, Grove Street and Exchange Place from $1,500/mo — 20–30% less than Manhattan. PATH to World Trade Center in 10 min. Month-to-month, all-inclusive pricing.',
};

// Título dinámico basado en la búsqueda
const pageTitle = computed(() => {
  const isEnglish = currentLanguage.value === 'en';
  const gsc = gscSearchMeta.value;
  if (gsc?.title) return gsc.title;

  // ── REGIÓN US: títulos con $ y "No Broker Fee"
  // Se activa tanto por IP (isUSRegion) como por ciudad (isUSCityPage),
  // lo que garantiza el título correcto durante el prerendering desde localhost.
  if ((isUSRegion.value || isUSCityPage.value) && props.isCitySpecific && props.currentCity) {
    if (usCityPageTitles[props.currentCity]) return usCityPageTitles[props.currentCity];
    const cityInfo = cityData[props.currentCity];
    const stableCount = cityInfo?.seoCount || 10;
    const minPrice = cityInfo?.priceRange?.split('-')[0] || '$1,200';
    const cityDisplay = props.currentCity === 'New York' ? 'NYC' : props.currentCity;
    return `${stableCount}+ Coliving in ${cityDisplay} — No Broker Fee, Rooms from ${minPrice}/mo | Coolliving`;
  }
  if (isUSRegion.value && !props.isCitySpecific) {
    return `${NYC_SEO_LISTINGS_LABEL} NYC Coliving Rooms — No Broker Fee, From $1,200/mo | Coolliving`;
  }

  // ── REGIÓN UK: títulos con £ y "No Agency Fees"
  // Se activa tanto por IP (isUKRegion) como por ciudad (isUKCityPage).
  if ((isUKRegion.value || isUKCityPage.value) && props.isCitySpecific && props.currentCity) {
    const cityInfo = cityData[props.currentCity];
    const stableCount = cityInfo?.seoCount || 5;
    const minPrice = cityInfo?.priceRange?.split('-')[0] || '£1,350';
    if (props.currentCity === 'London') {
      return `Coliving London 2026: ${LONDON_SEO_LISTINGS_LABEL} Furnished Rooms from £800/mo | Coolliving`;
    }
    const displayCity = `${props.currentCity}, London`;
    return `Coliving in ${displayCity} — No Agency Fees, From ${minPrice}/mo | Coolliving`;
  }
  if (isUKRegion.value && !props.isCitySpecific) {
    return `Coliving London 2026: ${LONDON_SEO_LISTINGS_LABEL} Rooms from £800/mo — No Agency Fees | Coolliving`;
  }

  // Páginas de residencias de estudiantes — título específico para este segmento
  if (isStudentSearch.value) {
    if (props.currentCity && studentCityPageTitles[props.currentCity]) {
      return studentCityPageTitles[props.currentCity];
    }
    const cityInfo = props.currentCity ? cityData[props.currentCity] : null;
    if (props.currentCity) {
      const minPrice = cityInfo?.priceRange?.split('-')[0]?.trim() || SEO_MIN_PRICE_ES;
      return `Residencia Estudiantes ${props.currentCity} 2026: Desde ${minPrice}/mes | Coolliving`;
    }
    return `Residencias universitarias España 2026 — RESA, Nido, Xior | Coolliving`;
  }

  // Hubs marketplace prerenderizados — títulos específicos por idioma/ruta
  if (isBaseColivingSearchPath.value) {
    if (currentLanguage.value === 'en') {
      return `Coliving NYC & London 2026 — ${NYC_SEO_LISTINGS_LABEL} Furnished Rooms (Brooklyn, Manhattan) | No Broker Fee | Coolliving`;
    }
    return `Alquiler de Habitaciones y Coliving en España — ${SEO_ROOMS_ES} desde ${SEO_MIN_PRICE_ES}/mes | Coolliving`;
  }

  // Para búsquedas GENÉRICAS de alta demanda (coliving barato, buscar coliving, etc.)
  // NOTA: Los títulos usan números ESTÁTICOS (1.100+) para evitar duplicados en Google.
  if (isGenericKeywordSearch.value) {
    const keywordType = genericKeywordType.value;
    
    if (isEnglish && keywordType === 'find') {
      // Título global: menciona España, NYC y London para no ahuyentar tráfico US/UK
      return `Find Coliving in Spain, NYC & London — ${SEO_ROOMS_EN} Verified Rooms | Coolliving`;
    }
    
    if (keywordType === 'barato') {
      return `Habitaciones Baratas y Coliving Económico: desde ${SEO_MIN_PRICE_MARKETING_ES}/mes — Alquiler Flexible (2026) | Coolliving`;
    }
    
    if (keywordType === 'buscar') {
      return `Buscar Habitaciones y Coliving en España: ${SEO_ROOMS_ES} Opciones Verificadas (2026) | Coolliving`;
    }
    
    if (keywordType === 'encontrar' || keywordType === 'encuentra') {
      return `Alquiler de Habitaciones y Coliving en España: ${SEO_ROOMS_ES} Verificadas desde ${SEO_MIN_PRICE_ES}/mes (2026) | Coolliving`;
    }
  }
  
  // Para búsquedas por PROVEEDOR (noindex, así que el título importa menos para SEO)
  if (props.isProviderSearch && props.currentProvider) {
    const provider = providerData[props.currentProvider];
    const providerName = provider?.name || props.currentProvider;
    
    if (isEnglish) {
      if (props.currentCity) {
        return `${providerName} in ${props.currentCity} — Rooms & Prices | Coolliving`;
      }
      return `${providerName}: Colivings & Prices in Spain | Coolliving`;
    } else {
      if (props.currentCity) {
        return `${providerName} en ${props.currentCity} — Habitaciones y Precios | Coolliving`;
      }
      return `${providerName}: Colivings y Precios en España | Coolliving`;
    }
  }
  
  // Para páginas específicas de ciudad CON filtro de precio (noindex, canonical a ciudad base)
  if (props.isCitySpecific && props.currentCity && props.maxPrice) {
    if (isEnglish) {
      return `Coliving in ${props.currentCity} ${priceLabelEN.value}: Private Rooms | Coolliving`;
    }
    return `Coliving en ${props.currentCity} ${priceLabel.value}: Habitaciones Privadas | Coolliving`;
  }
  
  // Para páginas específicas de ciudad SIN filtro de precio
  // TÍTULOS ESTÁTICOS: Usan seoCount (redondeado) para estabilidad en el índice de Google
  if (props.isCitySpecific && props.currentCity) {
    if (cityPageTitles[props.currentCity]) {
      return cityPageTitles[props.currentCity];
    }
    const cityInfo = cityData[props.currentCity]
    const stableCount = cityInfo?.seoCount || 10
    const minPrice = cityInfo?.priceRange?.split('-')[0] || '€300'
    
    if (isEnglish) {
      return `Coliving in ${props.currentCity}: ${stableCount}+ Private Rooms from ${minPrice}/mo (2026) | Coolliving`;
    }
    return `Coliving en ${props.currentCity}: ${stableCount}+ Habitaciones Privadas desde ${minPrice}/mes (2026) | Coolliving`;
  }
  
  // Para búsquedas dinámicas (texto libre, no prerenderizadas)
  if (props.searchLocation) {
    if (isEnglish) {
      return `Coliving in ${props.searchLocation}: Private Rooms & Prices (2026) | Coolliving`;
    }
    return `Coliving en ${props.searchLocation}: Habitaciones Privadas y Precios (2026) | Coolliving`;
  }
  
  // Título base de la página principal de búsqueda (sin ciudad ni filtros).
  // ESTÁTICO: 1.100+ es un número fijo redondeado, nunca cambia con cada búsqueda.
  // Keyword principal al inicio para CTR en SERP ("coliving", "coliving españa").
  if (isEnglish) {
    return `Find Coliving in Spain — ${SEO_ROOMS_EN} Rooms & Apartments from ${SEO_MIN_PRICE_EN}/mo | Coolliving`;
  }
  return `Alquiler de Habitaciones y Coliving en España — ${SEO_ROOMS_ES} desde ${SEO_MIN_PRICE_ES}/mes | Coolliving`;
})

// Descripción dinámica
const pageDescription = computed(() => {
  const isEnglish = currentLanguage.value === 'en';
  const gsc = gscSearchMeta.value;
  if (gsc?.description) return gsc.description;

  // ── REGIÓN US: descripciones siempre en inglés con $ ──────────────────
  // ── REGIÓN US: descripciones con $, activadas por IP o por nombre de ciudad
  if ((isUSRegion.value || isUSCityPage.value) && props.isCitySpecific && props.currentCity) {
    if (usCityPageDescriptions[props.currentCity]) return usCityPageDescriptions[props.currentCity];
    const cityInfo = cityData[props.currentCity];
    const stableCount = cityInfo?.seoCount || 10;
    const minPrice = cityInfo?.priceRange?.split('-')[0] || '$1,200';
    return `No broker fee. ${stableCount}+ furnished coliving rooms in ${props.currentCity}. From ${minPrice}/month, all-inclusive (WiFi, utilities, cleaning). Filter by neighborhood and price. Book online — move in this week.`;
  }
  if (isUSRegion.value && !props.isCitySpecific) {
    return `No broker fee. ${NYC_SEO_LISTINGS} furnished coliving rooms in Manhattan, Brooklyn, Queens & Jersey City. From $1,200/month, all-inclusive. Compare Furnished Quarters, Roomrs, SharedEasy, Common, JuneHomes — book online, move in this week.`;
  }

  // ── REGIÓN UK: descripciones con £, activadas por IP o por nombre de ciudad
  if ((isUKRegion.value || isUKCityPage.value) && props.isCitySpecific && props.currentCity) {
    const cityInfo = cityData[props.currentCity];
    const stableCount = cityInfo?.seoCount || 5;
    const minPrice = cityInfo?.priceRange?.split('-')[0] || '£1,350';
    const displayCity = props.currentCity === 'London' ? 'London' : `${props.currentCity}, London`;
    if (props.currentCity === 'London') {
      return `Coliving London 2026: ${LONDON_SEO_LISTINGS_LABEL} verified rooms in Shoreditch, Brixton, Camden, Wembley and more. From £800/mo all-inclusive, no agency fees. Compare ${LONDON_SEO_OPERATORS} operators and book online.`;
    }
    return `Compare ${stableCount}+ verified coliving spaces in ${displayCity}. Private furnished rooms from ${minPrice}/month, all-inclusive (WiFi, utilities, weekly cleaning). Filter by neighbourhood and price. Book 100% online — no agency fees.`;
  }
  if (isUKRegion.value && !props.isCitySpecific) {
    return `Coliving London 2026: ${LONDON_SEO_LISTINGS_LABEL} verified rooms in Shoreditch, Brixton, Camden, Wembley and more. From £800/mo all-inclusive, no agency fees. Compare ${LONDON_SEO_OPERATORS} operators and book online.`;
  }

  // Páginas de residencias de estudiantes
  if (isStudentSearch.value) {
    if (props.currentCity && studentCityPageDescriptions[props.currentCity]) {
      return studentCityPageDescriptions[props.currentCity];
    }
    const cityInfo = props.currentCity ? cityData[props.currentCity] : null;
    const minPrice = cityInfo?.priceRange?.split('-')[0]?.trim() || SEO_MIN_PRICE_ES;
    if (props.currentCity) {
      return `Residencias universitarias en ${props.currentCity} 2026: RESA, Nido, Xior, Livensa, Collegiate, Yugo y Urban Campus. Habitaciones desde ${minPrice}/mes todo incluido. Compara operadores verificados y reserva online sin comisiones.`;
    }
    return `Residencias universitarias en España: Madrid, Barcelona, Valencia, Málaga, Sevilla y Salamanca. Compara RESA, Nido, Xior, Livensa y más desde ${SEO_MIN_PRICE_ES}/mes todo incluido. Reserva 100% online sin comisiones.`;
  }

  // Hubs marketplace — snippet orientado a London/NYC (EN) o España (ES)
  if (isBaseColivingSearchPath.value) {
    if (currentLanguage.value === 'en') {
      return `No broker fee. ${NYC_SEO_LISTINGS} furnished coliving rooms in NYC — Brooklyn, Manhattan, Queens, Jersey City — plus ${LONDON_SEO_LISTINGS_LABEL} rooms in London. Month-to-month from $1,200/mo and £800/mo all-inclusive. Compare Roomrs, Common, June Homes, Cohabs — book online.`;
    }
    return `Compara ${SEO_ROOMS_ES} habitaciones, colivings y apartamentos amueblados en ${SEO_CITY_COUNT} ciudades de España desde ${SEO_MIN_PRICE_ES}/mes. Alquiler flexible todo incluido. Madrid, Barcelona, Valencia, Málaga y más.`;
  }

  // Para búsquedas GENÉRICAS de alta demanda
  // Descripciones ESTÁTICAS: no incluyen contadores dinámicos
  if (isGenericKeywordSearch.value) {
    const keywordType = genericKeywordType.value;
    
    if (isEnglish && keywordType === 'find') {
      return `Compare ${SEO_ROOMS_EN} verified coliving spaces and furnished apartments in Spain, New York City and London. Private rooms from ${SEO_MIN_PRICE_EN}/mo in Spain, from $1,200/mo in NYC, from £1,350/mo in London. All-inclusive, no tenant booking fees.`;
    }
    
    if (keywordType === 'barato') {
      return `Alquiler de habitaciones baratas y coliving económico en toda España desde ${SEO_MIN_PRICE_MARKETING_ES}/mes. Habitaciones privadas amuebladas, todo incluido (WiFi, limpieza semanal, gastos). Pisos compartidos y flex living en Madrid, Barcelona, Valencia y más. Reserva 100% online sin comisiones.`;
    }
    
    if (keywordType === 'buscar') {
      return `Busca habitaciones en alquiler y coliving en España. ${SEO_ROOMS_ES} opciones verificadas en ${SEO_CITY_COUNT} ciudades: Madrid, Barcelona, Valencia, Sevilla y más. Alquiler temporal y flexible desde ${SEO_MIN_PRICE_ES}/mes, todo incluido.`;
    }
    
    if (keywordType === 'encontrar' || keywordType === 'encuentra') {
      return `Compara ${SEO_ROOMS_ES} habitaciones, coliving y apartamentos amueblados en ${SEO_CITY_COUNT} ciudades de España desde ${SEO_MIN_PRICE_ES}/mes. Alquiler flexible todo incluido. Madrid, Barcelona, Valencia, Málaga y más.`;
    }
  }
  
  // Para búsquedas por PROVEEDOR (noindex, descripciones estáticas)
  if (props.isProviderSearch && props.currentProvider) {
    const provider = providerData[props.currentProvider];
    const providerName = provider?.name || props.currentProvider;
    const avgPrice = provider?.avgPrice || 650;
    
    if (isEnglish) {
      if (props.currentCity) {
        return `Explore ${providerName} colivings in ${props.currentCity}. Verified rooms from €${avgPrice}/month, transparent pricing and 100% online booking. Compare options commission-free on Coolliving.`;
      }
      return `Find all ${providerName} colivings in Spain. Compare prices, locations and amenities from €${avgPrice}/month. Book direct with no commission on Coolliving.`;
    } else {
      if (props.currentCity) {
        return `Explora los colivings de ${providerName} en ${props.currentCity}. Habitaciones verificadas desde ${avgPrice}€/mes, precios transparentes y reserva 100% online. Compara sin comisiones en Coolliving.`;
      }
      return `Encuentra todos los colivings de ${providerName} en España. Desde ${avgPrice}€/mes. Compara precios, ubicaciones y servicios. Reserva directa sin comisiones en Coolliving.`;
    }
  }
  
  // Para páginas específicas de ciudad CON filtro de precio (noindex)
  if (props.isCitySpecific && props.currentCity && props.maxPrice) {
    if (isEnglish) {
      return `Find rooms and coliving in ${props.currentCity} ${priceLabelEN.value}/month. Compare verified private rooms and furnished apartments — book 100% online, no commission fees.`;
    }
    return `Alquiler de habitaciones y coliving en ${props.currentCity} ${priceLabel.value}/mes. Compara habitaciones privadas y apartamentos amueblados verificados. Reserva online sin comisiones.`;
  }
  
  // Para páginas específicas de ciudad SIN filtro de precio.
  // Se priorizan descripciones optimizadas por ciudad (cityPageDescriptions) basadas
  // en datos de CTR de GSC. Fallback a plantilla genérica para ciudades no definidas.
  if (props.isCitySpecific && props.currentCity) {
    const cityInfo = cityData[props.currentCity]
    const stableCount = cityInfo?.seoCount || 10
    const minPrice = cityInfo?.priceRange?.split('-')[0] || '€300'
    
    if (isEnglish) {
      return `Your private room in ${props.currentCity} from ${minPrice}/month. All-inclusive: WiFi, bills & weekly cleaning. No hefty deposits or long contracts. ${stableCount}+ verified spaces. Book in 5 minutes! 🏠✨`;
    }
    // Usar descripción optimizada por ciudad si existe
    if (cityPageDescriptions[props.currentCity]) {
      return cityPageDescriptions[props.currentCity]
    }
    return `Tu habitación privada en ${props.currentCity} desde ${minPrice}/mes. Todo incluido: WiFi, gastos y limpieza semanal. Sin fianza abusiva ni contratos largos. ${stableCount}+ espacios verificados. ¡Reserva en 5 minutos! 🏠✨`;
  }
  
  // Para búsquedas dinámicas (texto libre)
  if (props.searchLocation) {
    if (isEnglish) {
      return `Find coliving and furnished rooms in ${props.searchLocation}. Private rooms and apartments, all-inclusive, from €400/month. Flexible rentals — 100% online booking, no commission fees.`;
    }
    return `Alquiler de habitaciones y coliving en ${props.searchLocation}. Habitaciones privadas amuebladas y apartamentos, todo incluido, desde €400/mes. Alquiler flexible sin comisiones.`;
  }
  
  // Página base genérica — snippet optimizado para CTR (~155 caracteres).
  // Objetivos: keyword al inicio, cifras ancla (1.100+, 14 ciudades, 450€), gratuito, ciudades top.
  if (isEnglish) {
    return `Compare ${SEO_ROOMS_EN} verified rooms, coliving & furnished apartments in ${SEO_CITY_COUNT} Spanish cities from ${SEO_MIN_PRICE_EN}/mo. All-inclusive flexible rentals. Madrid, Barcelona, Valencia & more.`;
  }
  return `Compara ${SEO_ROOMS_ES} habitaciones, coliving y apartamentos amueblados en ${SEO_CITY_COUNT} ciudades de España desde ${SEO_MIN_PRICE_ES}/mes. Alquiler flexible todo incluido. Madrid, Barcelona, Valencia y más.`
})

// NOTA: Meta keywords eliminadas - Google las ignora desde 2009
// Las FAQs se generan en ColivingSearchSchema.js dentro de getColivingSearchPageSchema()

// Imagen Open Graph dinámica por ciudad
const ogImage = computed(() => {
  const baseUrl = siteUrl.value;
  
  // Usar siempre la imagen OG principal de Coolliving
  // (En el futuro se pueden añadir imágenes específicas por ciudad)
  return `${baseUrl}/images/Coolliving_OG.png`;
})

// Schema de datos estructurados
const colivingSearchSchema = computed(() => {
  // Usar ciudad específica si está disponible, sino usar searchLocation
  const effectiveLocation = props.isCitySpecific && props.currentCity 
    ? props.currentCity 
    : props.searchLocation;
    
  return getColivingSearchPageSchema(siteUrl.value, {
    location: effectiveLocation,
    resultsCount: props.totalResults > 0 ? props.totalResults : props.searchResults.length,
    isCitySpecific: props.isCitySpecific,
    results: props.searchResultsForSchema,
    currentPage: 1,
    totalResults: props.totalResults,
    isStudentMode: isIndexedStudentPage.value,
  })
})

// ── Indexación: noindex en duplicados, filtros y páginas vacías ──────────
const FILTER_QUERY_KEYS = ['priceMin', 'priceMax', 'tipo', 'rooms', 'amenities', 'filter', 'q', 'page', 'sort']

const hasNoResults = computed(() => props.searchPerformed && props.searchResults.length === 0)

const isProviderWithCity = computed(() =>
  props.isProviderSearch && props.currentProvider && props.currentCity
)

const hasPriceFilter = computed(() => props.priceSlug != null)

const hasFilterQueryParams = computed(() =>
  FILTER_QUERY_KEYS.some((key) => route.query[key] !== undefined)
)

const isUnindexedStudentCity = computed(() =>
  isStudentSearch.value && props.currentCity && !isStudentCitySeoIndexed(props.currentCity)
)

/** UK/US en /encuentratucoliving/:ciudad — duplicado de findyourcoliving */
const isUKUSCityOnSpanishPath = computed(() =>
  currentLanguage.value === 'es'
  && props.isCitySpecific
  && props.currentCity
  && (isUSCityPage.value || isUKCityPage.value)
)

const shouldNoIndex = computed(() =>
  isAccommodationTypeSearch.value
  || isUnindexedStudentCity.value
  || isSpanishCityWithoutEnglishPath.value
  || isSpanishProviderOnEnglishPath.value
  || isUKUSCityOnSpanishPath.value
  || (!isStudentSearch.value && !isAccommodationTypeSearch.value && (
    hasNoResults.value
    || isProviderWithCity.value
    || hasPriceFilter.value
    || hasFilterQueryParams.value
  ))
)

/** Meta GSC solo en URLs indexables (canonical = self). Evita títulos agresivos en noindex/filtros. */
const gscSearchMeta = computed(() => {
  if (shouldNoIndex.value) return null;
  if (isStudentSearch.value || isGenericKeywordSearch.value || props.isProviderSearch) return null;

  let pageType = null;
  if (isBaseColivingSearchPath.value) pageType = 'hub';
  else if (props.isCitySpecific && props.currentCity) pageType = 'city';
  else return null;

  let region = 'es';
  if (isUKRegion.value || isUKCityPage.value) region = 'uk';
  else if (isUSRegion.value || isUSCityPage.value) region = 'us';

  const cityInfo = props.currentCity ? cityData[props.currentCity] : null;
  const minFromRange = cityInfo?.priceRange?.split('-')[0]?.trim();

  return getGscSearchMeta({
    pageType,
    lang: currentLanguage.value,
    region,
    city: props.currentCity,
    maxPrice: props.maxPrice,
    isStudentMode: isStudentSearch.value,
    metrics: {
      roomsEs: SEO_ROOMS_ES,
      minPriceEs: SEO_MIN_PRICE_ES,
      londonListings: LONDON_SEO_LISTINGS,
      londonListingsLabel: LONDON_SEO_LISTINGS_LABEL,
      londonOperators: LONDON_SEO_OPERATORS,
      nycListings: NYC_SEO_LISTINGS,
      nycListingsLabel: NYC_SEO_LISTINGS_LABEL,
      citySeoCount: cityInfo?.seoCount,
      cityMinPrice: minFromRange,
    },
  });
});

const robotsContent = computed(() =>
  shouldNoIndex.value ? 'noindex, follow' : 'index, follow'
)

const ogLocale = computed(() => {
  if (isUKRegion.value) return 'en_GB'
  if (isUSRegion.value) return 'en_US'
  return currentLanguage.value === 'en' ? 'en_US' : 'es_ES'
})

const ogLocaleAlt = computed(() => {
  if (isUSRegion.value || isUKRegion.value) return null
  return currentLanguage.value === 'en' ? 'es_ES' : 'en_US'
})

const headMeta = computed(() => {
  const tags = [
    { name: 'description', content: pageDescription.value },
    { name: 'robots', content: robotsContent.value },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: pageTitle.value },
    { property: 'og:description', content: pageDescription.value },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:image', content: ogImage.value },
    { property: 'og:locale', content: ogLocale.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: pageTitle.value },
    { name: 'twitter:description', content: pageDescription.value },
    { name: 'twitter:image', content: ogImage.value },
  ]

  if (!hasNoResults.value && !shouldNoIndex.value) {
    tags.push({ name: 'prerender-status-code', content: '200' })
  }
  if (ogLocaleAlt.value) {
    tags.push({ property: 'og:locale:alternate', content: ogLocaleAlt.value })
  }

  return tags
})

const headLinks = computed(() => {
  const links = [{ rel: 'canonical', href: canonicalUrl.value }]
  Object.entries(alternateUrls.value).forEach(([lang, url]) => {
    links.push({ rel: 'alternate', hreflang: lang, href: url })
  })
  return links
})

const headScripts = computed(() => [{
  type: 'application/ld+json',
  'data-coliving-search': 'true',
  innerHTML: JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': colivingSearchSchema.value,
  }),
}])

useHead({
  title: pageTitle,
  meta: headMeta,
  link: headLinks,
  script: headScripts,
})
</script>
